import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import _, { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { fetchSiteGroup as fetchSiteGroupById } from '@sophocles/api/siteGroup';
import HighlightedOptionRow from '@sophocles/components/HighlightedOptionRow';
import { fetchCustomerSiteGroupListWithFilters } from '@sophocles/entities/SiteGroup/siteGroupActions';

const getOptionLabel = option => {
  if (!option) {
    return 'Unknown';
  }
  const name = option.label;
  const description = option.description || '';
  if (description) {
    return `${name}. ${description}`;
  }
  return name;
};

const SiteGroupAutocomplete = ({
  customerId,
  name,
  value,
  size,
  error,
  helperText,
  required,
  fullWidth,
  disabled,
  onChange,
  onBlur,
  filters,
  label,
  fixedData = null,
  onSelectedChange = null,
  ignoreIds = [],
  excludeInventory = false,
  disablePortal = false,
  sx = [],
  variant = 'filled',
  hiddenLabel = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  // Default to the translated `siteGroups.siteGroup` term so
  // consumers that don't pass an explicit label still get the
  // tenant-aware copy ("Group" today).  Caller-supplied labels
  // win.
  const resolvedLabel = label ?? t('siteGroups.siteGroup', 'Group');
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const selectedRef = useRef(null);
  const dropdownFetched = useRef(false);
  const initialValueLoaded = useRef(false);

  const selectedOption = useMemo(() => {
    const found = options.find(opt => opt.value === value);
    if (found) {
      selectedRef.current = found;
      return found;
    }
    if (selectedRef.current?.value === value) return selectedRef.current;
    return null;
  }, [options, value]);

  useEffect(() => {
    if (selectedOption) {
      onSelectedChange?.(selectedOption);
    }
  }, [selectedOption, onSelectedChange]);

  const saveDataToState = useCallback(
    inputData => {
      const newData = inputData
        .filter(row => !ignoreIds.includes(row.id))
        .map(row => ({
          label: row.name,
          description: row.description || '',
          value: row.id,
          extra: row.extra,
        }));
      // Ensure cached selection stays in the list
      if (selectedRef.current && !newData.some(opt => opt.value === selectedRef.current.value)) {
        newData.unshift(selectedRef.current);
      }
      setOptions(newData);
    },
    [ignoreIds],
  );

  const fetchData = useCallback(
    debounce(() => {
      setIsLoading(true);
      dropdownFetched.current = true;
      if (fixedData && Array.isArray(fixedData)) {
        saveDataToState(fixedData);
        setIsLoading(false);
      } else if (customerId) {
        // An inventory group holds stock and never holds a site, so a picker
        // choosing where sites go asks for it to be left out. Filtered at the
        // fetch rather than in the rendered options, so paging and the "no
        // matches" state describe the list the caller actually offers.
        const requestFilters = excludeInventory ? { ...(filters || {}), is_inventory: false } : filters || {};
        dispatch(fetchCustomerSiteGroupListWithFilters(0, 1000, customerId, requestFilters, '', ['name']))
          .then(res => {
            const response = res.toJS();
            if (_.has(response, 'results')) {
              saveDataToState(response.results);
            } else if (Array.isArray(response)) {
              saveDataToState(response);
            } else {
              saveDataToState([]);
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        // No customer scope yet — nothing to fetch. Without this arm the
        // early setIsLoading(true) above spins forever.
        saveDataToState([]);
        setIsLoading(false);
      }
    }, 500),
    [dispatch, fixedData, customerId, filters, excludeInventory, saveDataToState],
  );

  // If mounted with a pre-selected value, fetch just that site group.
  // Full list loads when the dropdown is opened or customerId changes.
  useEffect(() => {
    if (value && !initialValueLoaded.current && !fixedData) {
      initialValueLoaded.current = true;
      setIsLoading(true);
      fetchSiteGroupById(value)
        .then(sg => {
          const opt = { label: sg.name, description: sg.description || '', value: sg.id, extra: sg.extra };
          selectedRef.current = opt;
          setOptions([opt]);
        })
        .catch(() => {
          fetchData();
        })
        .finally(() => setIsLoading(false));
    } else if (!initialValueLoaded.current) {
      initialValueLoaded.current = true;
      fetchData();
    }
  }, []);

  // Re-fetch when customerId changes (e.g. user picks a different customer)
  useEffect(() => {
    if (initialValueLoaded.current) {
      dropdownFetched.current = false;
      fetchData();
    }
  }, [customerId]);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      disablePortal={disablePortal}
      loading={isLoading}
      value={selectedOption}
      inputValue={inputValue}
      disabled={disabled}
      options={options}
      filterOptions={(options, state) => {
        const search = state.inputValue.trim().toLowerCase();
        if (!search) return options;
        return options.filter(
          opt => opt.label.toLowerCase().includes(search) || opt.description.toLowerCase().includes(search),
        );
      }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionLabel={option => getOptionLabel(option)}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      onOpen={() => {
        if (!dropdownFetched.current) {
          fetchData();
        }
      }}
      onBlur={onBlur}
      onChange={(e, opt) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        const siteGroupId = opt?.value;
        onChange(
          {
            ...e,
            preventDefault: () => {},
            stopPropagation: () => {},
            target: { id: `${name}-autocomplete-input`, name, value: siteGroupId },
          },
          siteGroupId,
          opt,
        );
      }}
      onInputChange={(e, value, reason) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        setInputValue(value);
        if (reason === 'input' && selectedOption != null) {
          onChange(e, null, null);
        }
      }}
      renderOption={(props, option) => (
        <Box
          {...props}
          key={option.value}
          sx={{
            width: '100%', // Ensure the container takes full width
            textAlign: 'left', // Aligns text properly
          }}
        >
          <HighlightedOptionRow label={option.label} description={option.description} searchText={inputValue} />
        </Box>
      )}
      renderInput={params => (
        <TextField
          id={`${name}-autocomplete-input`}
          {...params}
          required={required || false}
          fullWidth={fullWidth || false}
          name={name}
          size={size || 'small'}
          variant={variant}
          hiddenLabel={hiddenLabel}
          label={hiddenLabel ? undefined : resolvedLabel}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: isLoading ? <CircularProgress color="inherit" size={20} /> : params.InputProps.endAdornment,
          }}
        />
      )}
    />
  );
};

export default SiteGroupAutocomplete;
